import { useCallback, useEffect } from 'react'

// Closure variables shared across all hook instances
let activePreventions = 0
let isListenerActive = false

const beforeUnloadHandler = (event: BeforeUnloadEvent) => {
  event.preventDefault()
  event.returnValue =
    'Please do not refresh the page if a transaction is ongoing'
  return ''
}

/**
 * Hook that provides functions to prevent and allow page reloading/navigation.
 * This implementation shares state across multiple component instances.
 * The prevention is only removed when all instances have called allowPageReload or unmounted.
 *
 * Returns object with:
 * - preventPageReload: Function to start preventing page reload with a confirmation dialog
 * - allowPageReload: Function to remove this instance's prevention request
 */
export const usePreventPageReload = () => {
  const preventPageReload = useCallback(() => {
    activePreventions++

    if (activePreventions === 1 && !isListenerActive) {
      window.addEventListener('beforeunload', beforeUnloadHandler)
      isListenerActive = true
    }
  }, [])

  const allowPageReload = useCallback(() => {
    if (activePreventions > 0) {
      activePreventions--

      if (activePreventions === 0 && isListenerActive) {
        window.removeEventListener('beforeunload', beforeUnloadHandler)
        isListenerActive = false
      }
    }
  }, [])

  // Clean up when component unmounts
  useEffect(() => {
    return () => {
      allowPageReload()
    }
  }, [allowPageReload])

  return { preventPageReload, allowPageReload }
}
